import { BACKEND_URL } from '../config';
import { getDeviceSignature } from './deviceFingerprint';

export interface RetroDiffereSeason {
  id: number;
  legacyType: string;
  startsAt: string;
  endsAt: string;
  dealCount?: number;
  legacyParticipantCount: number;
}

export interface RetroPlayerSession {
  id: number;
  seasonId: number;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  virtualRank: number | null;
  totalRankedParticipants: number | null;
  decile: number | null;
  duplicateTotal: number | null;
  rawTotalScore: number | null;
}

export interface RetroSessionDeal {
  dealIndex: number;
  sourceDonneId: number;
  isAttackDeal: boolean;
  playOrder?: number;
  startedAt: string | null;
  finishedAt: string | null;
  rawScore: number | null;
  playable: boolean;
}

function headers(pseudo: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'x-user-pseudo': pseudo,
  };
}

async function parseJson<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (body as { error?: string }).error ?? res.statusText;
    throw new Error(err);
  }
  return (body as { data: T }).data;
}

export interface RetroDiffereTableInfo {
  season: RetroDiffereSeason | null;
  dealCount: number;
  activePlayerCount: number;
  activePlayers: string[];
  secondaryInfo: string;
}

function buildRetroSecondaryInfo(season: RetroDiffereSeason, dealCount: number): string {
  const start = new Date(season.startsAt).toLocaleDateString('fr-FR');
  const end = new Date(season.endsAt).toLocaleDateString('fr-FR');
  return `${dealCount} donnes · ${start} – ${end}`;
}

export async function fetchCurrentSeason(pseudo: string): Promise<RetroDiffereTableInfo | null> {
  const res = await fetch(`${BACKEND_URL}/api/competition/retro-differe/current-season`, {
    headers: headers(pseudo),
  });
  if (res.status === 404) return null;
  const body = await res.json();
  if (!body.success) throw new Error(body.error ?? 'api_error');
  const data = body.data as {
    season: RetroDiffereSeason;
    dealCount: number;
    session: RetroPlayerSession | null;
    activePlayerCount?: number;
    activePlayers?: string[];
  };
  return {
    season: data.season,
    dealCount: data.dealCount,
    activePlayerCount: data.activePlayerCount ?? 0,
    activePlayers: data.activePlayers ?? [],
    secondaryInfo: buildRetroSecondaryInfo(data.season, data.dealCount),
  };
}

export interface LegacyParticipationInfo {
  participated: boolean;
  officialRank: number | null;
  legacyDecile: number | null;
  participantLabel: string | null;
}

export interface RetroLeaderboardEntry {
  retroRank: number;
  pseudo: string;
  averagePercent: number;
  attackAveragePercent: number | null;
  defenseAveragePercent: number | null;
  virtualRank: number;
  totalParticipants: number;
  expectedRank: number | null;
  playedDealCount: number;
  totalDealCount: number;
  legacyOfficialRank: number | null;
  legacyAveragePercent: number | null;
  percentDelta: number | null;
  isReferenceBot?: boolean;
}

export interface RetroSeasonLeaderboard {
  top3: RetroLeaderboardEntry[];
  viewer: RetroLeaderboardEntry | null;
  referenceBot: RetroLeaderboardEntry | null;
  rankedPlayerCount: number;
  hasReferenceBot?: boolean;
}

/** Données complètes pour la page hub (session joueur incluse). */
export async function fetchCurrentSeasonForPlay(pseudo: string): Promise<{
  season: RetroDiffereSeason;
  dealCount: number;
  session: RetroPlayerSession | null;
  activePlayerCount: number;
  activePlayers: string[];
  legacyParticipation: LegacyParticipationInfo | null;
  leaderboard: RetroSeasonLeaderboard;
} | null> {
  const res = await fetch(`${BACKEND_URL}/api/competition/retro-differe/current-season`, {
    headers: headers(pseudo),
  });
  if (res.status === 404) return null;
  const body = await res.json();
  if (!body.success) throw new Error(body.error ?? 'api_error');
  return body.data;
}

export async function createSession(pseudo: string, seasonId: number): Promise<RetroPlayerSession> {
  const res = await fetch(`${BACKEND_URL}/api/competition/retro-differe/sessions`, {
    method: 'POST',
    headers: headers(pseudo),
    body: JSON.stringify({ seasonId }),
  });
  return parseJson(res);
}

export async function fetchSession(pseudo: string, sessionId: number): Promise<{
  session: RetroPlayerSession;
  deals: RetroSessionDeal[];
}> {
  const res = await fetch(`${BACKEND_URL}/api/competition/retro-differe/sessions/${sessionId}`, {
    headers: headers(pseudo),
  });
  return parseJson(res);
}

export async function fetchSessionDeals(pseudo: string, sessionId: number): Promise<RetroSessionDeal[]> {
  const res = await fetch(`${BACKEND_URL}/api/competition/retro-differe/sessions/${sessionId}/deals`, {
    headers: headers(pseudo),
  });
  return parseJson(res);
}

export async function finishSession(pseudo: string, sessionId: number): Promise<{ session: RetroPlayerSession }> {
  const res = await fetch(`${BACKEND_URL}/api/competition/retro-differe/sessions/${sessionId}/finish`, {
    method: 'POST',
    headers: headers(pseudo),
  });
  return parseJson(res);
}

export function retroDealFingerprintId(seasonId: number, dealIndex: number): string {
  return `retro:${seasonId}:${dealIndex}`;
}

export async function startDealFingerprint(
  pseudo: string,
  seasonId: number,
  dealIndex: number,
): Promise<{ allowed: boolean; warning?: string }> {
  const signature = await getDeviceSignature();
  const res = await fetch(`${BACKEND_URL}/api/competition/differe/start-deal`, {
    method: 'POST',
    headers: headers(pseudo),
    body: JSON.stringify({
      competition_type: 'retro_differe',
      deal_id: retroDealFingerprintId(seasonId, dealIndex),
      signature: {
        webVisitorId: signature.webVisitorId,
        nativeDeviceId: signature.nativeDeviceId,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      },
    }),
  });
  const body = await res.json();
  if (!res.ok && !body.allowed) {
    throw new Error(body.error ?? 'fingerprint_denied');
  }
  return { allowed: body.allowed !== false, warning: body.warning };
}
